import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import {
  type ImportPreviewResponse,
  type ImportPreviewRow,
} from "@fitness-app/data-models/entities/ProductClientToImport";

import { splitIntoChunks } from "@fitness-app/utils/src/helpers/splitIntoChunks";

import { productApi } from "~/store/initializeStore";

/** One preview request per distinct linked source ("" = no source, just the target product). */
export type PreviewGroups = Record<string, string[]>;

const PREVIEW_DEBOUNCE_MS = 500;
/** The endpoint validator caps one request at 2000 e-mails; a bigger batch goes in pieces. */
const PREVIEW_CHUNK_SIZE = 2000;

/**
 * Dry run of the batch as it currently stands (TRE-2110): who already holds
 * access in the target product and, per linked source, who has an access to
 * follow. Debounced — the manual rows are typed into.
 */
export const useImportPreview = (productId: string, groups: PreviewGroups, enabled: boolean) => {
  const [debouncedGroups, debounce] = useDebounce(groups, PREVIEW_DEBOUNCE_MS, {
    equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });

  const queries = useQueries({
    queries: Object.entries(debouncedGroups)
      .flatMap(([sourceProductId, emails]) =>
        splitIntoChunks(emails, PREVIEW_CHUNK_SIZE).map((chunk) => [sourceProductId, chunk] as const),
      )
      .map(([sourceProductId, emails]) => ({
        queryKey: ["importPreview", productId, sourceProductId, [...emails].sort().join(",")],
        enabled: enabled && Boolean(productId),
        staleTime: 30_000,
        queryFn: async () => {
          const { data } = await productApi.post<ImportPreviewResponse>(`/products/${productId}/imports/preview`, {
            emails,
            sourceProductId: sourceProductId || null,
          });

          return { sourceProductId, rows: data.rows };
        },
      })),
  });

  const isError = queries.some((query) => query.isError);

  const rowsByKey = useMemo(() => {
    const map = new Map<string, ImportPreviewRow>();

    for (const query of queries) {
      for (const row of query.data?.rows ?? []) {
        map.set(`${query.data?.sourceProductId ?? ""}|${row.email}`, row);
      }
    }

    return map;
  }, [queries]);

  const lookup = (email: string, sourceProductId: string | null) =>
    rowsByKey.get(`${sourceProductId ?? ""}|${email.trim().toLowerCase()}`) ?? null;

  // `isPending`: rows changed but the request has not gone out yet — the caller
  // must not queue the batch against the previous preview in that window.
  const isFetching = queries.some((query) => query.isFetching) || debounce.isPending();

  return { lookup, isFetching, isError, ready: !isFetching && queries.length > 0 };
};
